import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, cardsCollection, logAnalyticsEvent } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { 
  query, 
  where, 
  onSnapshot, 
  getDocs, 
  writeBatch, 
  addDoc, 
  serverTimestamp,
  collection,
  getDoc,
  doc,
  getFirestore,
  enableIndexedDbPersistence,
  updateDoc,
  arrayUnion
} from 'firebase/firestore';
import Login from './components/Login';
import Flashcard, { createCardContent } from './components/Flashcard';
import CardStack from './components/CardStack';
import AddCardForm from './components/AddCardForm';
import EditCardForm from './components/EditCardForm';
import Logo from './components/Logo';
import QuizButton from './components/QuizButton';
import { AuthProvider } from './contexts/AuthContext';
import ConnectPage from './pages/ConnectPage';
import { Link, Routes, Route } from 'react-router-dom';
import HamburgerMenu from './components/HamburgerMenu';
import Header from './components/Header';
import ShareModal from './components/ShareModal';
import { toast, Toaster } from 'react-hot-toast';
import { testFirebaseConnection } from './utils/firebaseUtils';
import { FirestoreDebugger } from './components/FirestoreDebugger';
import { getUserProfile, createUserProfile, acceptTerms } from './services/userService';
import ProfileCompletionModal from './components/ProfileCompletionModal';
import { shareGroup, getAccessibleGroups, getSharedGroups, shareCard, getSharedCards } from './services/sharingService';
import ConnectionsList from './components/ConnectionsList';
import PhotoAlbum from './components/PhotoAlbum';
import PrivateRoute from './components/PrivateRoute';
import Modal from './components/Modal';
import BulkUpload from './components/BulkUpload';
import BulkUploadModal from './components/BulkUploadModal';
import FacialRecognition from './components/FacialRecognition';
import { initFaceApi } from './utils/faceApiInit';
import { useNavigate } from 'react-router-dom';
import CardDetail from './pages/CardDetail';
import { Clock, SortAsc } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import LoadingState from './components/LoadingState';
import QuizMode from './components/QuizMode';
import TermsAndConditionsModal from './components/TermsAndConditionsModal';
import SupportPage from './pages/SupportPage';
import AuthCheck from './components/AuthCheck';
import Folder from './components/Folder';
import FolderModal from './components/FolderModal';
import { createFolder, getUserFolders, renameFolder, deleteFolder, moveGroupToFolder } from './services/folderService';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingGroup, setSharingGroup] = useState(null);
  const [sharingCards, setSharingCards] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [cardsPerGroup, setCardsPerGroup] = useState({});
  const [showPhotoAlbum, setShowPhotoAlbum] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showFacialRecognition, setShowFacialRecognition] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [sharingCard, setSharingCard] = useState(null);
  const navigate = useNavigate();
  const [groupSortOrder, setGroupSortOrder] = useState('recent');
  const [cardSortOrder, setCardSortOrder] = useState('recent');
  const [sortOrder, setSortOrder] = useState('recent');
  const [isLoading, setIsLoading] = useState(true);
  const [showQuizMode, setShowQuizMode] = useState(false);
  const [folders, setFolders] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [moveGroupModal, setMoveGroupModal] = useState(null);

  const handleSortToggle = (newOrder) => {
    console.log('Changing sort order to:', newOrder);
    setSortOrder(newOrder);
  };

  const handleGroupSortToggle = (newOrder) => {
    console.log('Changing group sort order to:', newOrder);
    setGroupSortOrder(newOrder);
  };

  // Move this function outside of useEffect, after the state declarations and before the useEffects
  const loadAllCards = async () => {
    if (!user) return;

    try {
      // Get user's own cards
      const myCardsQuery = query(
        collection(db, 'cards'),
        where('userId', '==', user.uid)
      );
      const myCardsSnapshot = await getDocs(myCardsQuery);
      const myCards = myCardsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get shared cards
      const sharedCards = await getSharedCards(user.uid);
      
      // Create a Set of card IDs to track duplicates
      const seenIds = new Set();
      const uniqueCards = [];

      // Process own cards first
      myCards.forEach(card => {
        if (!seenIds.has(card.id)) {
          seenIds.add(card.id);
          uniqueCards.push(card);
        }
      });

      // Then add shared cards if they haven't been seen
      sharedCards.forEach(card => {
        if (!seenIds.has(card.id)) {
          seenIds.add(card.id);
          uniqueCards.push(card);
        }
      });

      setCards(uniqueCards);
    } catch (error) {
      console.error('Error loading cards:', error);
    }
  };

  // Initialize Firestore once when the component mounts
  useEffect(() => {
    const initializeFirestore = async () => {
      try {
        // await enableIndexedDbPersistence(db);
        console.log('Firestore initialized');
      } catch (err) {
        console.error("Error initializing Firestore:", err);
      }
    };
    initializeFirestore();
  }, []);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed - User:', user?.uid);
      setUser(user);
      setLoading(false);
      setAuthLoading(false);
      if (user) {
        // Log user login
        logAnalyticsEvent('user_login', {
          userId: user.uid,
          userEmail: user.email
        });
      }
    });
    return () => unsubscribe();
  }, []);

  // Cards and shared items listener
  useEffect(() => {
    if (!user) return;

    const loadAllCards = async () => {
      try {
        // Get user's own cards
        const myCardsQuery = query(
          collection(db, 'cards'),
          where('userId', '==', user.uid)
        );
        const myCardsSnapshot = await getDocs(myCardsQuery);
        const myCards = myCardsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Get shared cards
        const sharedCards = await getSharedCards(user.uid);
        
        // Create a Set of card IDs to track duplicates
        const seenIds = new Set();
        const uniqueCards = [];

        // Process own cards first
        myCards.forEach(card => {
          if (!seenIds.has(card.id)) {
            seenIds.add(card.id);
            uniqueCards.push(card);
          }
        });

        // Then add shared cards if they haven't been seen
        sharedCards.forEach(card => {
          if (!seenIds.has(card.id)) {
            seenIds.add(card.id);
            uniqueCards.push(card);
          }
        });

        setCards(uniqueCards);
      } catch (error) {
        console.error('Error loading cards:', error);
      }
    };

    // Initial load
    loadAllCards();

    // Set up real-time listener for changes
    const unsubscribe = onSnapshot(
      query(collection(db, 'cards'), where('userId', '==', user.uid)),
      () => loadAllCards()
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const initializeUserProfile = async () => {
      if (!user) {
        console.log('No user, skipping profile check');
        return;
      }
      
      try {
        console.log('Checking profile for user:', user.uid);
        const profile = await getUserProfile(user.uid);
        console.log('Profile data:', profile);
        
        if (!profile) {
          console.log('No profile found, creating new one');
          await createUserProfile(user);
          setShowProfileModal(true);
        } else if (!profile.profileCompleted) {
          console.log('Incomplete profile found, showing modal');
          setShowProfileModal(true);
        } else {
          console.log('Profile is complete');
        }
      } catch (error) {
        console.error('Error initializing user profile:', error);
      }
    };

    if (user && !authLoading) {
      console.log('User authenticated, checking profile');
      initializeUserProfile();
    }
  }, [user, authLoading]);

  useEffect(() => {
    const loadGroups = async () => {
      setIsLoading(true);
      if (!user) return;
      
      try {
        // Get all theoretically accessible groups
        const accessibleGroups = await getAccessibleGroups(user.uid);
        console.log('Raw accessible groups:', accessibleGroups);
        
        // Get all non-deleted cards
        const allCards = cards.filter(card => !card.deleted);
        console.log('All non-deleted cards:', allCards);
        
        // Create a Map to store unique groups by their id
        const activeGroupsMap = new Map();
        const groupToCardFolderIds = {};
        
        // First add all accessible groups
        accessibleGroups.forEach(group => {
          if (typeof group === 'string') {
            activeGroupsMap.set(group, { id: group, name: group });
          } else if (group && typeof group === 'object') {
            // Ensure the group has both id and name
            const groupId = group.id || group.name;
            activeGroupsMap.set(groupId, { 
              id: groupId, 
              name: group.name || groupId,
              folderId: group.folderId
            });
          }
        });
        
        // Then check cards for additional groups and track folderIds
        allCards.forEach(card => {
          // Handle single group
          if (card.group) {
            if (!activeGroupsMap.has(card.group)) {
              activeGroupsMap.set(card.group, { id: card.group, name: card.group });
            }
            if (!groupToCardFolderIds[card.group]) groupToCardFolderIds[card.group] = [];
            groupToCardFolderIds[card.group].push(card.folderId || null);
          }
          // Handle groups array
          if (Array.isArray(card.groups)) {
            card.groups.forEach(g => {
              if (!activeGroupsMap.has(g)) {
                activeGroupsMap.set(g, { id: g, name: g });
              }
              if (!groupToCardFolderIds[g]) groupToCardFolderIds[g] = [];
              groupToCardFolderIds[g].push(card.folderId || null);
            });
          }
          // Handle sharedGroups
          if (card.sharedGroups) {
            card.sharedGroups.forEach(g => {
              if (!activeGroupsMap.has(g)) {
                activeGroupsMap.set(g, { id: g, name: g });
              }
              if (!groupToCardFolderIds[g]) groupToCardFolderIds[g] = [];
              groupToCardFolderIds[g].push(card.folderId || null);
            });
          }
        });
        
        // Assign folderId to each group if all cards in the group have the same folderId (and it's not null)
        activeGroupsMap.forEach((groupObj, groupId) => {
          // Find all cards that belong to this group (as primary or in groups/sharedGroups)
          const cardsInGroup = allCards.filter(card => {
            if (card.group === groupId) return true;
            if (Array.isArray(card.groups) && card.groups.includes(groupId)) return true;
            if (Array.isArray(card.sharedGroups) && card.sharedGroups.includes(groupId)) return true;
            return false;
          });
          // For those cards, collect their folderIds
          const folderIds = cardsInGroup.map(card => card.folderId).filter(Boolean);
          const uniqueFolderIds = Array.from(new Set(folderIds));
          // Only assign folderId if all cards in the group have the same non-null folderId and there is at least one card
          if (cardsInGroup.length > 0 && uniqueFolderIds.length === 1 && folderIds.length === cardsInGroup.length) {
            groupObj.folderId = uniqueFolderIds[0];
          } else {
            groupObj.folderId = undefined;
          }
        });
        
        // Convert map values to array and ensure all groups are properly formatted and valid
        const activeGroups = Array.from(activeGroupsMap.values())
          .filter(group => group && typeof group === 'object' && group.id && group.name)
          .map(group => ({
            id: group.id,
            name: group.name,
            folderId: group.folderId
          }));
        
        console.log('Final active groups:', activeGroups);
        setGroups(activeGroups);
        
      } catch (error) {
        console.error('Error loading groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadGroups();
    }
  }, [user, cards]);

  console.log('Raw cards from database:', cards);
  console.log('Processed groups:', groups);
  console.log('Cards per group:', cardsPerGroup);

  // Test connection before rendering
  useEffect(() => {
    testFirebaseConnection().then(success => {
      if (!success) {
        console.error('Unable to connect to Firebase');
      }
    });
  }, []); // Run once on mount

  // Add model initialization effect
  useEffect(() => {
    const loadFaceModels = async () => {
      try {
        console.log('Initializing face detection models...');
        const initialized = await initFaceApi();
        if (initialized) {
          console.log('Face detection models initialized successfully');
        } else {
          console.error('Failed to initialize face detection models');
        }
      } catch (error) {
        console.error('Error initializing face detection models:', error);
      }
    };

    loadFaceModels();
  }, []); // Empty dependency array means this runs once when component mounts

  // Add sort toggle button component
  const SortToggle = ({ sortOrder, onToggle, className }) => (
    <div className={`inline-flex rounded-md shadow-sm ${className}`}>
      <button
        onClick={() => onToggle('recent')}
        className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
          sortOrder === 'recent'
            ? 'bg-[#04b53a] text-white border-[#04b53a]'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        <Clock className="w-4 h-4 inline-block mr-1" />
        Recent
      </button>
      <button
        onClick={() => onToggle('alpha')}
        className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b -ml-px ${
          sortOrder === 'alpha'
            ? 'bg-[#04b53a] text-white border-[#04b53a]'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        <SortAsc className="w-4 h-4 inline-block mr-1" />
        A-Z
      </button>
    </div>
  );

  const getTimestamp = (date) => {
    if (!date) return 0;
    // Handle Firestore Timestamp
    if (typeof date.toMillis === 'function') {
      return date.toMillis();
    }
    // Handle JavaScript Date
    if (date instanceof Date) {
      return date.getTime();
    }
    // Handle numeric timestamp
    if (typeof date === 'number') {
      return date;
    }
    return 0;
  };

  const sortGroups = (groupsToSort) => {
    return [...groupsToSort].sort((a, b) => {
      if (groupSortOrder === 'alpha') {
        return a.name.localeCompare(b.name);
      } else {
        // For recent sorting, find the most recent card in each group
        const getGroupLastModified = (groupName) => {
          const groupCards = cards.filter(card => {
            if (card.deleted) return false;
            const cardGroups = new Set();
            if (card.group) cardGroups.add(card.group);
            if (Array.isArray(card.groups)) {
              card.groups.forEach(g => cardGroups.add(g));
            }
            if (card.isShared && card.sharedGroups) {
              card.sharedGroups.forEach(g => cardGroups.add(g));
            }
            return cardGroups.has(groupName);
          });

          return Math.max(...groupCards.map(card => {
            const updatedAt = getTimestamp(card.updatedAt);
            const createdAt = getTimestamp(card.createdAt);
            return Math.max(updatedAt, createdAt);
          }), 0);
        };

        return getGroupLastModified(b.name) - getGroupLastModified(a.name);
      }
    });
  };

  const sortCards = (cards, sortOrder) => {
    console.log('Sorting cards with order:', sortOrder);
    
    return [...cards].sort((a, b) => {
      if (sortOrder === 'alpha') {
        // Sort alphabetically by first name
        const nameA = (a.firstName || '').toLowerCase();
        const nameB = (b.firstName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else {
        // Sort by timestamp (recent first)
        const getTimestamp = (card) => {
          const createdAt = card.createdAt;
          if (!createdAt) return 0;
          
          // Handle different timestamp formats
          if (typeof createdAt === 'string') {
            return new Date(createdAt).getTime();
          } else if (createdAt instanceof Date) {
            return createdAt.getTime();
          } else if (typeof createdAt.toDate === 'function') {
            return createdAt.toDate().getTime();
          }
          return 0;
        };

        return getTimestamp(b) - getTimestamp(a);
      }
    });
  };

  // Add this function after loadAllCards
  const handleCreateCardFromFace = async (imageData) => {
    if (!user) return;
    
    try {
      // Upload image to storage
      const imageRef = ref(storage, `card-images/${user.uid}/${Date.now()}.jpg`);
      const imageBlob = await fetch(imageData).then(r => r.blob());
      await uploadBytes(imageRef, imageBlob);
      const imageUrl = await getDownloadURL(imageRef);

      // Create new card
      const newCard = {
        userId: user.uid,
        imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        groups: ['New Faces'],
      };

      const docRef = await addDoc(collection(db, 'cards'), newCard);
      
      // Reload cards
      loadAllCards();
      
      // Switch to the new card's group
      setSelectedGroup('New Faces');
      
      // Open edit dialog for the new card
      setEditingCard({ id: docRef.id, ...newCard });
    } catch (error) {
      console.error('Error creating card from face:', error);
    }
  };

  // Add this function to format cards into groups for QuizMode
  const formatCardsForQuiz = (selectedGroup = null) => {
    // If a specific group is selected, only format cards for that group
    if (selectedGroup) {
      const groupCards = cards.filter(card => {
        if (card.deleted) return false;
        const cardGroups = new Set();
        if (card.group) cardGroups.add(card.group);
        if (Array.isArray(card.groups)) {
          card.groups.forEach(g => cardGroups.add(g));
        }
        if (card.isShared && card.sharedGroups) {
          card.sharedGroups.forEach(g => cardGroups.add(g));
        }
        return cardGroups.has(selectedGroup);
      });

      return [{
        id: selectedGroup,
        name: selectedGroup,
        people: groupCards.map(card => ({
          id: card.id,
          firstName: card.firstName || '',
          lastName: card.lastName || '',
          imageUrl: card.imageUrl,
          details: card.details || '',
          groupName: selectedGroup
        }))
      }];
    }

    // Original group mapping logic for when no specific group is selected
    const groupMap = new Map();
    
    cards.forEach(card => {
      if (card.deleted) return;
      
      // Get all groups this card belongs to
      const cardGroups = new Set();
      if (card.group) cardGroups.add(card.group);
      if (Array.isArray(card.groups)) {
        card.groups.forEach(g => cardGroups.add(g));
      }
      if (card.isShared && card.sharedGroups) {
        card.sharedGroups.forEach(g => cardGroups.add(g));
      }
      
      // Format card data
      const cardData = {
        id: card.id,
        firstName: card.firstName || '',
        lastName: card.lastName || '',
        imageUrl: card.imageUrl,
        details: card.details || '',
        // Add any other relevant card data here
      };
      
      // Add card to each of its groups
      cardGroups.forEach(groupName => {
        if (!groupMap.has(groupName)) {
          groupMap.set(groupName, {
            id: groupName, // Using group name as ID
            name: groupName,
            people: []
          });
        }
        groupMap.get(groupName).people.push(cardData);
      });
    });
    
    // Convert map to array
    return Array.from(groupMap.values());
  };

  // Add this to handle starting quiz mode
  const handleStartQuiz = (selectedGroup = null) => {
    const quizGroups = formatCardsForQuiz(selectedGroup);
    console.log('Starting quiz with groups:', quizGroups);
    if (quizGroups.length > 0) {
      setShowQuizMode(true);
      setModalContent(
        <QuizMode
          groups={quizGroups}
          onClose={() => {
            setShowQuizMode(false);
            setModalContent(null);
          }}
        />
      );
    } else {
      toast.error('No cards available for quiz');
    }
  };

  useEffect(() => {
    if (!user) return;
    getUserFolders(user.uid).then(setFolders);
  }, [user]);

  useEffect(() => {
    console.log('showFolderModal changed:', showFolderModal);
  }, [showFolderModal]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Logo className="w-12 h-12 animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  if (showProfileModal && user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfileCompletionModal
          user={user}
          onComplete={() => setShowProfileModal(false)}
          checkTerms={true}
          onTermsAccept={async () => {
            await acceptTerms(user.uid);
          }}
        />
      </div>
    );
  }

  const handleGroupShare = async (targetUserId, groupName, withEditAccess = false) => {
    if (!user) return;
    
    try {
      console.log(`Attempting to share group ${groupName} with user ${targetUserId}`);
      
      // Get all cards in this group before sharing
      const groupCards = cards.filter(card => {
        return card.group === groupName || 
               (Array.isArray(card.groups) && card.groups.includes(groupName));
      });
      
      console.log(`Found ${groupCards.length} cards in group ${groupName}`);
      
      // Share the group first
      await shareGroup(user.uid, targetUserId, groupName, withEditAccess);
      
      // Share each card in the group
      for (const card of groupCards) {
        try {
          await shareCard(user.uid, targetUserId, card, withEditAccess);
          console.log(`Shared card ${card.id} with user ${targetUserId}`);
        } catch (cardError) {
          console.error(`Error sharing card ${card.id}:`, cardError);
        }
      }
      
      toast.success(`Successfully shared group "${groupName}" with ${groupCards.length} cards`);
      console.log(`Successfully shared group ${groupName} and ${groupCards.length} cards with user ${targetUserId}`);
      
    } catch (error) {
      console.error('Error sharing group:', error);
      toast.error(error.message || 'Failed to share group');
      throw error;
    }
  };

  const handleFacialMatch = (card) => {
    console.log('Matched card:', card);
    const modalContent = (
      <div className="p-6 max-w-lg mx-auto">
        {card.imageUrl && (
          <div className="flex justify-center mb-4">
            <img 
              src={card.imageUrl} 
              alt={`${card.firstName} ${card.lastName}`}
              className="w-24 h-24 rounded-full object-cover border-2 border-[#04b53a]"
            />
          </div>
        )}
        {createCardContent(card)}
      </div>
    );
    
    setShowFacialRecognition(false);
    toast.success(`Found match: ${card.firstName} ${card.lastName}`);
    setModalContent(modalContent);
    setShowModal(true);
  };

  const handleCreateCard = async (cardData) => {
    try {
      // Create the card document
      const newCardRef = await addDoc(cardsCollection, {
        ...cardData,
        userId: user.uid,
        createdAt: serverTimestamp(),
        deleted: false
      });

      // Log card creation analytics
      logAnalyticsEvent('card_created', {
        userId: user.uid,
        cardId: newCardRef.id
      });

      // Close the form and refresh data
      setShowAddForm(false);
      loadAllCards();
    } catch (error) {
      console.error('Error creating card:', error);
      toast.error('Failed to create card');
    }
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardShare = async (recipientId, card, withEditAccess = false) => {
    try {
      if (!user || !recipientId || !card) {
        console.error('Missing required data:', { user, recipientId, card });
        throw new Error('Missing required data for sharing');
      }
      
      console.log('Sharing card:', card, 'with user:', recipientId, 'withEditAccess:', withEditAccess);
      await shareCard(user.uid, recipientId, card, withEditAccess);
      toast.success('Card shared successfully!');
    } catch (error) {
      console.error('Error sharing card:', error);
      toast.error(error.message || 'Failed to share card');
      throw error; // Re-throw to be caught by ShareModal
    }
  };

  const handleCreateFolder = async (name) => {
    const newFolder = await createFolder(user.uid, name);
    setFolders(prev => [...prev, newFolder]);
  };

  const handleRenameFolder = async (folder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleFolderSubmit = async (name) => {
    if (editingFolder) {
      await renameFolder(editingFolder.id, name);
      setFolders(prev => prev.map(f => f.id === editingFolder.id ? { ...f, name } : f));
    } else {
      await handleCreateFolder(name);
    }
    setShowFolderModal(false);
    setEditingFolder(null);
  };

  const handleDeleteFolder = async (folder) => {
    await deleteFolder(folder.id);
    setFolders(prev => prev.filter(f => f.id !== folder.id));
  };

  const ungroupedGroups = groups.filter(group => !group.folderId);

  return (
    <AuthCheck user={user}>
      <div className="min-h-screen bg-gray-50">
        <Toaster position="bottom-center" />
        
        <Header 
          user={user}
          onAddCardClick={() => setShowAddForm(true)}
          onBulkUploadClick={() => setShowBulkUpload(true)}
          onFacialRecognitionClick={() => setShowFacialRecognition(true)}
          onQuizClick={handleStartQuiz}
          cards={cards}
          groups={groups}
          selectedGroup={selectedGroup}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-2 mb-4">
            {showAddForm && (
              <AddCardForm
                onClose={() => setShowAddForm(false)}
                userId={user.uid}
                existingGroups={groups}
              />
            )}
          </div>

          {selectedGroup ? (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <button
                  onClick={() => setSelectedGroup(null)}
                  className="flex items-center text-[#04b53a] hover:text-[#039431] transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Groups
                </button>
                <SortToggle
                  sortOrder={sortOrder}
                  onToggle={handleSortToggle}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortCards(cards.filter(card => {
                  // Skip deleted cards
                  if (card.deleted) return false;
                  
                  // Skip cards that we own but are sharing with others
                  // (they'll show up in their respective groups)
                  if (card.userId === user.uid && card.isShared) return false;
                  
                  const cardGroups = new Set();
                  if (card.group) cardGroups.add(card.group);
                  if (Array.isArray(card.groups)) {
                    card.groups.forEach(g => cardGroups.add(g));
                  }
                  if (card.isShared && card.sharedGroups) {
                    card.sharedGroups.forEach(g => cardGroups.add(g));
                  }
                  return cardGroups.has(selectedGroup);
                }), sortOrder).length === 0 ? (
                  <div className="col-span-full text-center text-gray-500">
                    No cards in this group yet.
                  </div>
                ) : (
                  sortCards(cards.filter(card => {
                    // Skip deleted cards
                    if (card.deleted) return false;
                    
                    // Skip cards that we own but are sharing with others
                    // (they'll show up in their respective groups)
                    if (card.userId === user.uid && card.isShared) return false;
                    
                    const cardGroups = new Set();
                    if (card.group) cardGroups.add(card.group);
                    if (Array.isArray(card.groups)) {
                      card.groups.forEach(g => cardGroups.add(g));
                    }
                    if (card.isShared && card.sharedGroups) {
                      card.sharedGroups.forEach(g => cardGroups.add(g));
                    }
                    return cardGroups.has(selectedGroup);
                  }), sortOrder).map(card => {
                    const cardContent = createCardContent(card, (cardId) => {
                      const targetCard = cards.find(c => c.id === cardId);
                      if (targetCard) {
                        setEditingCard(null); // Close any open edit form
                        setSelectedGroup(targetCard.groups?.[0] || targetCard.group); // Switch to target card's group
                        // Use setTimeout to ensure group switch happens before scrolling
                        setTimeout(() => {
                          const element = document.getElementById(`card-${cardId}`);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }, 100);
                      }
                    });
                    return (
                      <div key={card.id} id={`card-${card.id}`} className="w-full flex justify-center">
                        <Flashcard 
                          frontContent={
                            card.imageUrl ? (
                              <img
                                src={card.imageUrl}
                                alt={`${card.firstName} ${card.lastName}`}
                                className="w-48 h-48 object-cover rounded-full"
                              />
                            ) : (
                              <div className="w-48 h-48 bg-gray-200 rounded-full flex items-center justify-center">
                                <span className="text-gray-400">No image available</span>
                              </div>
                            )
                          }
                          backContent={cardContent}
                          onEdit={(card) => {
                            console.log('Edit triggered for card:', card);
                            setEditingCard(card);
                          }}
                          originalCard={card}
                          onShare={handleCardShare}
                          currentUser={user}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="col-span-full flex justify-between items-center mb-4">
                <button
                  onClick={() => setShowFolderModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#04b53a] rounded-md hover:bg-[#039431]"
                >
                  New Folder
                </button>
                <SortToggle
                  sortOrder={groupSortOrder}
                  onToggle={handleGroupSortToggle}
                />
              </div>
              {isLoading ? (
                <div className="col-span-full">
                  <LoadingState />
                </div>
              ) : ungroupedGroups.length === 0 ? (
                <div className="col-span-full text-center text-gray-500 mt-8">
                  No card groups yet...
                </div>
              ) : (
                sortGroups(ungroupedGroups)
                  .filter(groupObj => groupObj && typeof groupObj === 'object' && groupObj.id)
                  .map(groupObj => (
                    <CardStack
                      key={groupObj.id}
                      group={groupObj}
                      cards={cards.filter(card => {
                        if (card.deleted) return false;
                        const cardGroups = new Set();
                        if (card.group) cardGroups.add(card.group);
                        if (Array.isArray(card.groups)) {
                          card.groups.forEach(g => cardGroups.add(g));
                        }
                        if (card.isShared && card.sharedGroups) {
                          card.sharedGroups.forEach(g => cardGroups.add(g));
                        }
                        return cardGroups.has(groupObj.name);
                      })}
                      onCardClick={handleGroupSelect}
                      onShareClick={handleGroupShare}
                      currentUser={user}
                      onMoveGroup={group => setMoveGroupModal({ groupId: group.id })}
                    />
                  ))
              )}
            </div>
          )}
        </div>

        {showBulkUpload && (
          <BulkUploadModal
            isOpen={showBulkUpload}
            onClose={() => setShowBulkUpload(false)}
            userId={user.uid}
            existingGroups={groups}
          />
        )}

        <Modal isOpen={showPhotoAlbum && !authLoading && user} onClose={() => setShowPhotoAlbum(false)}>
          <PhotoAlbum 
            user={user} 
            onClose={() => setShowPhotoAlbum(false)} 
          />
        </Modal>

        <Modal isOpen={showFacialRecognition} onClose={() => setShowFacialRecognition(false)}>
          <FacialRecognition 
            cards={cards}
            onMatch={handleFacialMatch}
            onClose={() => setShowFacialRecognition(false)}
            onCreateCard={handleCreateCardFromFace}
          />
        </Modal>

        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          {modalContent}
        </Modal>

        <Modal isOpen={showQuizMode} onClose={() => setShowQuizMode(false)}>
          <QuizMode
            groups={formatCardsForQuiz(selectedGroup)}
            onClose={() => setShowQuizMode(false)}
          />
        </Modal>

        <Routes>
          <Route 
            path="/flashcard/:id" 
            element={
              <Flashcard 
                cards={cards}
                onShare={handleCardShare}
                currentUser={user}
              />
            } 
          />
          <Route path="/support" element={<SupportPage />} />
        </Routes>

        {/* Only show folders when not viewing a specific group */}
        {!selectedGroup && folders.map(folder => (
          <Folder
            key={folder.id}
            folder={folder}
            groups={groups.filter(group => group.folderId === folder.id)}
            cards={cards}
            onGroupClick={handleGroupSelect}
            onShareClick={handleGroupShare}
            currentUser={user}
            onAddGroup={() => setShowAddForm(true)}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            groupSortOrder={groupSortOrder}
          />
        ))}

        {moveGroupModal && (
          <Modal isOpen={!!moveGroupModal} onClose={() => setMoveGroupModal(null)}>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Move Group to Folder</h2>
              {folders.length === 0 ? (
                <div>No folders available.</div>
              ) : (
                <ul>
                  {folders.map(folder => (
                    <li key={folder.id} className="mb-2">
                      <button
                        className="w-full text-left px-4 py-2 rounded hover:bg-gray-100"
                        onClick={async () => {
                          await moveGroupToFolder(moveGroupModal.groupId, folder.id);
                          setMoveGroupModal(null);
                          // Reload cards, which will trigger useEffect to reload groups
                          await loadAllCards();
                        }}
                      >
                        {folder.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                className="mt-4 px-4 py-2 bg-gray-200 rounded"
                onClick={() => setMoveGroupModal(null)}
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}

        <Modal isOpen={!!editingCard} onClose={() => setEditingCard(null)}>
          {editingCard && (
            <EditCardForm
              card={editingCard}
              onClose={() => {
                console.log('Closing edit form');
                setEditingCard(null);
              }}
              existingGroups={groups.map(g => g.name)}
              currentUserId={user.uid}
              cards={cards}
            />
          )}
        </Modal>
      </div>

      <FolderModal
        isOpen={showFolderModal}
        onClose={() => {
          console.log('Closing folder modal');
          setShowFolderModal(false);
          setEditingFolder(null);
        }}
        onSubmit={handleFolderSubmit}
        folder={editingFolder}
        title={editingFolder ? 'Rename Folder' : 'Create Folder'}
      />
    </AuthCheck>
  );
}

export default App;